'use client';

/**
 * useSudoMode — Step-up authentication / Sudo Mode (M4).
 *
 * The Go API gates sensitive routes (project/workspace/account delete,
 * billing checkout, api-token mint, email change) on a fresh sudo
 * grant. When the grant is missing or expired the API returns 412
 * Precondition Failed with `{ code: "sudo_mode_required",
 * actionClass: "billing|project_delete|...", grantDurationSeconds:
 * 900 }`. The client opens a modal, prompts the user to re-auth via
 * passkey UV / TOTP / password (priority order), submits the credential
 * to /api/auth/sudo/challenge, and on success transparently retries the
 * original request.
 *
 * Wiring:
 *
 *   1. In any high-level layout that lives inside the authenticated
 *      app, render <SudoChallengeListener /> once. It listens for the
 *      `zenopus:sudo-required` window event that fetchWithAuth emits
 *      whenever an API call comes back 412 with `code` ===
 *      `sudo_mode_required` or `sudo_mode_locked`.
 *
 *   2. The listener calls openSudoModal() exposed by useSudoMode —
 *      hooks into a SudoChallengeModal (see components/auth) that
 *      drives the re-auth flow and resolves the original promise.
 *
 *   3. Callers that want the transparent retry behaviour wrap their
 *      mutating fetch in `runWithSudoMode(callback)`. The callback is
 *      invoked once normally; on 412 it is invoked again exactly once
 *      after the user completes the modal.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithAuth } from '@/lib/utils/api-auth';

/**
 * Event name emitted on `window` whenever a fetchWithAuth call comes
 * back with a 412 Precondition Failed response carrying
 * `code === 'sudo_mode_required'` or `'sudo_mode_locked'`. The
 * SudoChallengeListener subscribes to it; multiple subscribers are
 * supported but in practice only one listener should be mounted per
 * authenticated layout.
 */
export const SUDO_REQUIRED_EVENT = 'zenopus:sudo-required' as const;

export type SudoActionClass =
  | 'billing'
  | 'project_delete'
  | 'workspace_delete'
  | 'api_token_create'
  | 'account_delete'
  | 'email_change'
  | 'admin_revoke';

export type SudoMethod = 'password' | 'passkey-uv' | 'totp';

export type SudoRequiredEventDetail = {
  /** The action class the server expected re-auth for. */
  actionClass: SudoActionClass;
  /** TTL of the grant once successfully refreshed (seconds). */
  grantDurationSeconds: number;
  /** When the lockout cooldown ends (seconds). 0 if not locked. */
  retryAfterSeconds: number;
  /** True when the failure is the per-user lockout, not a missing grant. */
  locked: boolean;
};

export type SudoChallengeMethodsResponse = {
  methods: SudoMethod[];
  sudoModeUntil: string;
  lockedUntil: string;
  /** Map of action class -> grant TTL in seconds. */
  durations: Record<string, number>;
};

export type SudoVerifyRequest = {
  method: SudoMethod;
  actionClass: SudoActionClass;
  credentials: {
    password?: string;
    totpCode?: string;
  };
  /** When method === 'passkey-uv', supply the X-Passkey-UV-Assertion header. */
  passkeyAssertionHeader?: string;
};

export type SudoVerifyResponse = {
  granted: boolean;
  method: SudoMethod;
  actionClass: SudoActionClass;
  sudoModeUntil: string;
};

export type SudoModeApiError = {
  code: string;
  message?: string;
  retryAfterSeconds?: number;
};

const SUDO_LIST_ENDPOINT = '/api/auth/sudo/challenge';
const SUDO_VERIFY_ENDPOINT = '/api/auth/sudo/challenge';

/**
 * Public hook. Components that need to render the sudo modal (e.g.
 * SudoChallengeListener at the app root) call useSudoMode() and bind
 * the returned `state`/`open`/`close`/`verify` to a SudoChallengeModal
 * instance.
 *
 * Components that just want to *protect* a destructive action wrap
 * their fetch with `runWithSudoMode()` — the hook subscribes to the
 * window event for them and resolves the returned promise once the
 * grant is refreshed (or rejects when the user cancels).
 */
// Module-level mount counter. Lets fetchWithAuth (lib/utils/api-auth.ts)
// detect the silent-failure mode where a sudo_mode_required 412 fires
// but no <SudoChallengeListener /> is mounted to surface it. The flag
// is only checked at dispatch time, so the regression manifests as a
// loud console.error in dev rather than a confusing "delete failed"
// toast for the user (which is how the 2026-05-15 production incident
// went unnoticed — backend gating shipped, frontend mount forgotten).
let mountedSudoListenerCount = 0;

export function isSudoListenerMounted(): boolean {
  return mountedSudoListenerCount > 0;
}

export function useSudoMode() {
  const [pending, setPending] = useState<SudoRequiredEventDetail | null>(null);
  const [methods, setMethods] = useState<SudoMethod[] | null>(null);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  // Subscribe to the global event so a single mount of useSudoMode at
  // the app root catches every 412 from any fetchWithAuth call.
  // The mountedSudoListenerCount bookkeeping is the regression-guard
  // for the silent-failure mode described next to the module-level
  // counter above.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<SudoRequiredEventDetail>).detail;
      if (!detail) return;
      setPending(detail);
      setError(null);
    };
    window.addEventListener(SUDO_REQUIRED_EVENT, handler);
    mountedSudoListenerCount += 1;
    return () => {
      window.removeEventListener(SUDO_REQUIRED_EVENT, handler);
      mountedSudoListenerCount -= 1;
    };
  }, []);

  // When a sudo challenge becomes pending, fetch the available methods
  // so the modal can render the right tabs. We do NOT memoise the
  // response across openings — the user might enroll a passkey in
  // another tab between two sudo prompts. The fetch is dispatched via
  // a ref-based "pending change counter" so the effect itself only
  // subscribes to external state (the network) and the React state
  // updates land inside the async callback (idiomatic React 19
  // `react-hooks/set-state-in-effect` shape).
  const cancelledRef = useRef(false);
  useEffect(() => {
    if (!pending) {
      // No challenge open → no work. Reset is owned by close() and
      // verify() so the effect stays free of cascading setState calls.
      return undefined;
    }
    cancelledRef.current = false;
    // Defer the fetch via setTimeout(0) so the synchronous effect
    // body does not contain any direct setState calls (React 19
    // react-hooks/set-state-in-effect rule). The actual network fetch
    // is an external-system synchronization, which the rule allows.
    const handle = setTimeout(() => {
      void (async () => {
        setMethodsLoading(true);
        try {
          const response = await fetchWithAuth(SUDO_LIST_ENDPOINT, { method: 'GET' });
          if (cancelledRef.current) return;
          if (!response.ok) {
            setError('auth.sudoMode.errors.listMethodsFailed');
            setMethods([]);
            return;
          }
          const body = (await response.json()) as { data?: SudoChallengeMethodsResponse };
          const list = body.data?.methods ?? [];
          setMethods(list);
          if (list.length === 0) {
            setError('auth.sudoMode.errors.noMethodAvailable');
          }
        } catch {
          if (!cancelledRef.current) {
            setError('auth.sudoMode.errors.networkError');
            setMethods([]);
          }
        } finally {
          if (!cancelledRef.current) setMethodsLoading(false);
        }
      })();
    }, 0);
    return () => {
      clearTimeout(handle);
      cancelledRef.current = true;
    };
  }, [pending]);

  /**
   * Cancel the pending challenge. The original request that triggered
   * it stays failed (its caller decides how to surface the error).
   * Resets methods/error here (not in the methods-fetch effect) so the
   * effect stays free of cascading setState calls.
   */
  const close = useCallback(() => {
    setPending(null);
    setError(null);
    setMethods(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('zenopus:sudo-cancelled'));
    }
  }, []);

  /**
   * Submit a re-auth attempt. On success the modal closes and resolves
   * with the new sudoModeUntil so callers can retry the original
   * request. On failure the error is set and the modal stays open so
   * the user can try a different method.
   */
  const verify = useCallback(async (input: SudoVerifyRequest): Promise<SudoVerifyResponse> => {
    setVerifying(true);
    setError(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (input.method === 'passkey-uv' && input.passkeyAssertionHeader) {
        headers['X-Passkey-UV-Assertion'] = input.passkeyAssertionHeader;
      }
      const response = await fetchWithAuth(SUDO_VERIFY_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          method: input.method,
          actionClass: input.actionClass,
          credentials: input.credentials ?? {},
        }),
      });
      const body = await response.json();
      if (!response.ok || !body.success) {
        const code = (body.error ?? body.code ?? 'invalid_credentials') as string;
        if (code === 'sudo_mode_locked') {
          setError('auth.sudoMode.errors.locked');
        } else if (code === 'two_factor_invalid') {
          setError('auth.sudoMode.errors.invalidTotp');
        } else if (code === 'uv_reauth_required') {
          setError('auth.sudoMode.errors.passkeyFailed');
        } else {
          setError('auth.sudoMode.errors.invalidCredentials');
        }
        throw new Error(code);
      }
      const result = body.data as SudoVerifyResponse;
      // Successful — close the modal and dispatch a follow-up event
      // so any pending retry-loop (runWithSudoMode) can re-enter.
      setPending(null);
      setMethods(null);
      setError(null);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('zenopus:sudo-granted', { detail: result }));
      }
      return result;
    } finally {
      setVerifying(false);
    }
  }, []);

  /**
   * `runWithSudoMode` — convenience wrapper. Run `callback` once;
   * if it throws SudoRequiredError or returns a 412, await the modal
   * resolution and re-run the callback exactly once. Designed for
   * direct use inside button click handlers:
   *
   *   await runWithSudoMode(async () => {
   *     const r = await fetchWithAuth(`/api/projects/${id}`, { method: 'DELETE' });
   *     if (r.status === 412) throw new SudoRequiredError();
   *     // …
   *   });
   */
  const runWithSudoMode = useCallback(async <T>(callback: () => Promise<T>): Promise<T> => {
    try {
      return await callback();
    } catch (err) {
      if (!(err instanceof SudoRequiredError)) {
        throw err;
      }
    }
    // Wait for sudo-granted dispatch.
    await new Promise<void>((resolve, reject) => {
      const onGrant = () => {
        cleanup();
        resolve();
      };
      const onCancel = () => {
        cleanup();
        reject(new Error('sudo_cancelled'));
      };
      const cleanup = () => {
        window.removeEventListener('zenopus:sudo-granted', onGrant);
        window.removeEventListener('zenopus:sudo-cancelled', onCancel);
      };
      window.addEventListener('zenopus:sudo-granted', onGrant);
      window.addEventListener('zenopus:sudo-cancelled', onCancel);
    });
    return callback();
  }, []);

  return useMemo(
    () => ({
      pending,
      methods,
      methodsLoading,
      error,
      verifying,
      close,
      verify,
      runWithSudoMode,
    }),
    [pending, methods, methodsLoading, error, verifying, close, verify, runWithSudoMode],
  );
}

/**
 * Sentinel error used by `runWithSudoMode` callers to signal "this
 * request returned 412 sudo-required". The wrapper catches it,
 * suspends until the modal resolves, then retries the callback. Other
 * errors propagate untouched.
 */
export class SudoRequiredError extends Error {
  constructor() {
    super('sudo_mode_required');
    this.name = 'SudoRequiredError';
  }
}
